import React, { useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout, { TopBar } from '../components/Layout';
import { Meta, Display, Chip, Rule, Button } from '../components/Atoms';
import { LoadingState, ErrorState } from '../components/states';
import { useApi } from '../lib/api';

/**
 * PlantProblemDetail - szczegóły konkretnego problemu rośliny.
 *
 * Endpoint: GET /api/plant-problems/:id
 * Response: { problem, solutions[] }
 *
 * Sekcje:
 *   - Hero z severity + category
 *   - Objawy
 *   - Rośliny dotknięte (chipy)
 *   - Sezon zagrożenia
 *   - Rozwiązania (produkty z effectiveness, dose, karencja)
 */

const CATEGORY_LABEL = {
  szkodnik: 'Szkodnik',
  choroba_grzybowa: 'Choroba grzybowa',
  choroba_bakteryjna: 'Choroba bakteryjna',
  niedobor: 'Niedobór',
};

const CATEGORY_ICON = {
  szkodnik: '🐛',
  choroba_grzybowa: '🍄',
  choroba_bakteryjna: '🦠',
  niedobor: '⚗',
};

const SEVERITY_TONE = {
  krytyczna: 'rust',
  wysoka: 'rust',
  średnia: 'amber',
  niska: 'sage',
};

// Slugi typu "przy_objawach" → "Przy objawach"
function humanize(s) {
  if (!s || typeof s !== 'string') return s;
  const t = s.replace(/_/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// Zwraca pierwszy fragment notes przed " - " lub "." (np. "Nawóz azotowy" z "Nawóz azotowy - mocznik...")
function titleFromNotes(notes) {
  if (!notes) return null;
  const m = notes.match(/^([^\-.]+)/);
  return m ? m[1].trim() : null;
}

// Mapa best_stage → częstotliwość w dniach (dla auto-recurring care)
const STAGE_INTERVAL = {
  co_7_dni: 7,
  co_10_dni: 10,
  co_14_dni: 14,
  co_3_dni: 3,
  co_5_dni: 5,
  co_tydzień: 7,
  co_dwa_tygodnie: 14,
};

function SolutionCard({ solution }) {
  const eff = solution.effectiveness;
  const isReco = solution.is_recommended === 1 || solution.is_recommended === true;
  const isProduct = !!solution.product_name;

  const title = solution.product_name
    || solution.non_chemical_method
    || titleFromNotes(solution.notes)
    || 'Metoda alternatywna';

  const careType = isProduct ? 'spray' : 'fertilization';
  const careName = isProduct ? solution.product_name : title;
  const intervalDays = STAGE_INTERVAL[solution.best_stage];

  const careParams = new URLSearchParams({ type: careType });
  if (careName) careParams.set('name', careName);
  if (solution.withdrawal_period != null) careParams.set('withdrawal', String(solution.withdrawal_period));
  if (intervalDays) careParams.set('repeat_every', String(intervalDays));
  if (solution.notes) careParams.set('note', solution.notes);

  return (
    <div style={{
      padding: '18px 20px', borderRadius: 12,
      background: 'var(--t-bg-elev)',
      border: `1px solid ${isReco ? 'var(--t-sage)' : 'var(--t-line)'}`,
      borderLeft: `3px solid ${isReco ? 'var(--t-sage)' : 'var(--t-amber)'}`,
      marginBottom: 10,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
        marginBottom: 8,
      }}>
        <Display size={18} style={{ display: 'inline', letterSpacing: '-.01em' }}>
          {title}
        </Display>
        {isReco && <Chip tone="sage">Polecane</Chip>}
        {solution.is_ecological === 1 && <Chip tone="sage">Ekologiczne</Chip>}
        {!isProduct && <Chip tone="dim">Metoda agrotechniczna</Chip>}
        {solution.product_type && (
          <span style={{
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.16em', textTransform: 'uppercase',
            color: 'var(--t-text-mute)',
          }}>{humanize(solution.product_type)}</span>
        )}
      </div>

      {solution.active_substance && (
        <div style={{
          fontSize: 12, color: 'var(--t-text-dim)',
          fontStyle: 'italic', marginBottom: 10,
        }}>Substancja czynna: {solution.active_substance}</div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 10, marginBottom: 12,
        fontSize: 12,
      }}>
        {eff && (
          <div>
            <Meta style={{ marginBottom: 2 }}>Skuteczność</Meta>
            <span style={{ color: 'var(--t-text)' }}>{humanize(eff)}</span>
          </div>
        )}
        {solution.speed && (
          <div>
            <Meta style={{ marginBottom: 2 }}>Czas działania</Meta>
            <span style={{ color: 'var(--t-text)' }}>{humanize(solution.speed)}</span>
          </div>
        )}
        {(solution.dosage_2l || solution.dosage_5l) && (
          <div>
            <Meta style={{ marginBottom: 2 }}>Dawka</Meta>
            <span style={{ color: 'var(--t-text)', fontFamily: 'var(--t-mono)' }}>
              {solution.dosage_2l || ''}{solution.dosage_5l ? ` · ${solution.dosage_5l}` : ''}
            </span>
          </div>
        )}
        {solution.withdrawal_period != null && (
          <div>
            <Meta style={{ marginBottom: 2 }}>Karencja</Meta>
            <span style={{
              color: solution.withdrawal_period > 7 ? 'var(--t-rust)' : 'var(--t-amber)',
              fontFamily: 'var(--t-mono)',
            }}>{solution.withdrawal_period} dni</span>
          </div>
        )}
        {solution.best_stage && (
          <div>
            <Meta style={{ marginBottom: 2 }}>Najlepszy moment</Meta>
            <span style={{ color: 'var(--t-text)' }}>{humanize(solution.best_stage)}</span>
          </div>
        )}
      </div>

      {solution.notes && (
        <div style={{
          padding: '10px 12px', borderRadius: 8,
          background: 'var(--t-bg)',
          fontSize: 12, color: 'var(--t-text-dim)',
          fontStyle: 'italic', lineHeight: 1.5,
          marginBottom: 12,
        }}>↳ {solution.notes}</div>
      )}

      {solution.warnings && (
        <div style={{
          padding: '10px 12px', borderRadius: 8,
          background: 'rgba(192, 106, 74, 0.08)',
          borderLeft: '2px solid var(--t-rust)',
          fontSize: 12, color: 'var(--t-text)',
          lineHeight: 1.5, marginBottom: 12,
        }}>⚠ {solution.warnings}</div>
      )}

      <Link to={`/care/add?${careParams.toString()}`} style={{ textDecoration: 'none' }}>
        <Button variant="ghost">
          {isProduct ? 'Zaplanuj zabieg' : 'Odnotuj zabieg'}
          {intervalDays ? ` (powtarzaj co ${intervalDays} dni)` : ''} →
        </Button>
      </Link>
    </div>
  );
}

export default function PlantProblemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [reportMsg, setReportMsg] = useState('');
  // Skąd user przyszedł — żeby wiedzieć dokąd "Wróć". PlantDetail przekazuje
  // `?from=plant_<id>`. Bez tego fallback do listy problemów.
  const fromPlant = searchParams.get('from'); // np. "plant_801"
  const fromPlantId = fromPlant?.startsWith('plant_') ? fromPlant.slice(6) : null;

  const handleBack = () => {
    if (fromPlantId) navigate(`/plants/${fromPlantId}`);
    else if (window.history.length > 1) navigate(-1);
    else navigate('/problemy');
  };

  const { data, loading, error } = useApi(`/api/plant-problems/${id}`);

  if (error?.status === 401) {
    return <Layout><ErrorState error={error} /></Layout>;
  }
  if (loading) return <Layout><LoadingState /></Layout>;
  if (error || !data?.problem) {
    return (
      <Layout>
        <div style={{ maxWidth: 480, margin: '60px auto', padding: 24, textAlign: 'center' }}>
          <Display size={32}>Problem nie istnieje</Display>
          <div style={{ marginTop: 16, marginBottom: 24, color: 'var(--t-text-dim)' }}>
            Ten wpis mógł zostać usunięty albo URL jest błędny.
          </div>
          <Link to="/problemy" style={{ textDecoration: 'none' }}>
            <Button variant="primary">← Wróć do listy</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const { problem, solutions = [] } = data;
  const tone = SEVERITY_TONE[problem.severity] || 'dim';
  const icon = CATEGORY_ICON[problem.category] || '⚠';
  const affects = (problem.affects_plants || '').split(',').map(p => p.trim()).filter(Boolean);

  return (
    <Layout topBar={<TopBar
      left={
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, fontSize: 13,
        }}>
          <button
            type="button"
            onClick={handleBack}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 8,
              background: 'var(--t-bg-elev)',
              border: '1px solid var(--t-line)',
              color: 'var(--t-text)', cursor: 'pointer',
              fontSize: 13, fontFamily: 'inherit',
            }}
          >← Wróć</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--t-text-dim)' }}>
            <Link to="/problemy" style={{ color: 'var(--t-text-dim)', textDecoration: 'none' }}>Problemy</Link>
            <span style={{ opacity: 0.5 }}>/</span>
            <span style={{ color: 'var(--t-sage)' }}>
              {CATEGORY_LABEL[problem.category] || problem.category}
            </span>
          </div>
        </div>
      }
    />}>
      <div style={{
        maxWidth: 760, margin: '0 auto',
        padding: '32px 32px 60px',
      }}>
        {/* Hero */}
        <div style={{
          display: 'flex', gap: 20, alignItems: 'flex-start',
          marginBottom: 28, paddingBottom: 24,
          borderBottom: '1px solid var(--t-line)',
        }}>
          <div style={{
            fontSize: 56, lineHeight: 1, flexShrink: 0,
          }}>{icon}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              marginBottom: 8,
            }}>
              <Chip tone={tone}>{problem.severity}</Chip>
              <Chip tone="dim">{CATEGORY_LABEL[problem.category] || problem.category}</Chip>
            </div>
            <Display size={'clamp(36px, 6vw, 48px)'} style={{
              display: 'block', letterSpacing: '-.03em', lineHeight: 1,
              marginBottom: 8,
            }}>{problem.name}</Display>
            {problem.common_name && (
              <div style={{
                fontSize: 14, color: 'var(--t-text-dim)',
                fontStyle: 'italic',
              }}>też nazywany: {problem.common_name}</div>
            )}
          </div>
        </div>

        {/* Opis */}
        {problem.description && (
          <div style={{
            fontSize: 16, color: 'var(--t-text-dim)',
            lineHeight: 1.65, marginBottom: 28,
            paddingLeft: 18,
            borderLeft: '2px solid var(--t-line-2)',
          }}>{problem.description}</div>
        )}

        {/* Objawy */}
        {problem.symptoms && (
          <>
            <Rule label="I — objawy" />
            <div style={{
              padding: '20px 24px', borderRadius: 12,
              background: 'var(--t-bg-elev)',
              border: '1px solid var(--t-line)',
              marginBottom: 28,
              fontSize: 14, color: 'var(--t-text)',
              lineHeight: 1.65,
              whiteSpace: 'pre-wrap',
            }}>{problem.symptoms}</div>
          </>
        )}

        {/* Dotyczy roślin */}
        {affects.length > 0 && (
          <>
            <Rule label="II — dotyczy roślin" />
            <div style={{
              display: 'flex', gap: 8, flexWrap: 'wrap',
              marginBottom: 28,
            }}>
              {affects.map(p => (
                <span key={p} style={{
                  padding: '8px 14px', borderRadius: 999,
                  background: 'var(--t-bg-elev)',
                  border: '1px solid var(--t-line)',
                  color: 'var(--t-text)',
                  fontFamily: 'var(--t-mono)', fontSize: 12,
                  letterSpacing: '.06em',
                }}>{p}</span>
              ))}
            </div>
          </>
        )}

        {/* Sezon */}
        {problem.peak_season && (
          <>
            <Rule label="III — sezon zagrożenia" />
            <div style={{
              padding: '14px 18px', borderRadius: 12,
              background: 'rgba(220, 180, 100, 0.06)',
              borderLeft: '3px solid var(--t-amber)',
              fontSize: 13, color: 'var(--t-text)',
              marginBottom: 28,
            }}>
              ↻ {problem.peak_season.replace(/,/g, ' · ').replace(/_/g, ' ')}
            </div>
          </>
        )}

        {/* Rozwiązania */}
        <Rule label={`IV — rozwiązania ${solutions.length > 0 ? `(${solutions.length})` : ''}`} />
        {solutions.length > 0 ? (
          <div style={{ marginBottom: 28 }}>
            {solutions.map(s => (
              <SolutionCard key={s.solution_id} solution={s} />
            ))}
          </div>
        ) : (
          <div style={{
            padding: '24px', borderRadius: 12,
            border: '1px dashed var(--t-line-2)',
            textAlign: 'center', color: 'var(--t-text-mute)',
            fontSize: 13, fontStyle: 'italic',
            marginBottom: 28,
          }}>↳ Brak zarejestrowanych rozwiązań chemicznych. Skonsultuj się z literaturą fachową albo lokalnym ogrodnikiem.</div>
        )}

        {/* Zgłoś w okolicy (T11) */}
        <div style={{ marginBottom: 28 }}>
          <button
            type="button"
            onClick={async () => {
              try {
                const axios = (await import('../../config/axios')).default;
                await axios.post(`/api/plant-problems/${problem.id}/report`);
                setReportMsg('✓ Zgłoszono — dziękujemy!');
              } catch (e) {
                setReportMsg('Nie udało się zgłosić: ' + (e.response?.data?.error || e.message));
              }
            }}
            style={{
              padding: '10px 18px', borderRadius: 10,
              background: 'var(--t-bg-elev)',
              border: '1px solid var(--t-line)',
              color: 'var(--t-text)',
              fontFamily: 'var(--t-sans)', fontSize: 13,
              cursor: 'pointer', transition: 'all 200ms ease',
            }}
          >📢 Zgłoś ten problem w okolicy</button>
          {reportMsg && (
            <div style={{
              marginTop: 8, fontSize: 12, color: reportMsg.startsWith('✓') ? 'var(--t-sage)' : 'var(--t-rust)',
            }}>{reportMsg}</div>
          )}
          <div style={{
            marginTop: 6, fontSize: 11, color: 'var(--t-text-mute)',
            fontStyle: 'italic',
          }}>Anonimowo · lokalizacja zaokrąglona do ~10 km</div>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 30, paddingTop: 20,
          borderTop: '1px solid var(--t-line)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 12,
          fontFamily: 'var(--t-mono)', fontSize: 11,
          letterSpacing: '.16em', textTransform: 'uppercase',
          color: 'var(--t-text-mute)',
        }}>
          <Link to="/problemy" style={{ color: 'var(--t-sage)', textDecoration: 'none' }}>
            ← Wszystkie problemy
          </Link>
          <span>Zawsze sprawdź instrukcję preparatu</span>
        </div>
      </div>
    </Layout>
  );
}
